import { Injectable, inject, signal } from '@angular/core';

import { Observable, catchError, of, shareReplay, tap, throwError } from 'rxjs';

import { CoverageCategory } from '../models/package-configurator/coverage-option.model';
import { PackageConfiguratorService } from '../services/package-configurator.service';

/**
 * Root cache for the coverage-categories dictionary. Categories are fetched from the backend once and
 * reused across the package view/edit page: `load()` returns them from the store when present, otherwise
 * makes a single request (shared so concurrent callers don't trigger duplicate calls).
 */
@Injectable({ providedIn: 'root' })
export class CoverageCategoriesStore {
  private readonly service = inject(PackageConfiguratorService);

  private readonly categoriesSignal = signal<CoverageCategory[]>([]);
  private loaded = false;
  /** In-flight/completed shared request — guards against parallel loads issuing multiple calls. */
  private request$?: Observable<CoverageCategory[]>;

  /** Cached categories (empty until the first successful load). */
  readonly categories = this.categoriesSignal.asReadonly();

  /** Categories from the store; fetches once if not loaded yet, and reuses the cache afterwards. */
  load(): Observable<CoverageCategory[]> {
    if (this.loaded) return of(this.categoriesSignal());

    this.request$ ??= this.service.getCoverageCategories().pipe(
      tap(categories => {
        this.categoriesSignal.set(categories);
        this.loaded = true;
      }),
      catchError(error => {
        // Drop the cached request so a later load() can retry after a failure.
        this.request$ = undefined;
        return throwError(() => error);
      }),
      shareReplay(1)
    );

    return this.request$;
  }
}
